import {TranslateService, zDateTime, zNumberToString} from '@brunata-angular/util';
import {z} from 'zod';
import {FormControl, FormGroup} from '@angular/forms';
import {
    AGENT_SCHEDULES,
    CONSUMPTION_AGENT_COMPARISON_PERIODS,
    CONSUMPTION_AGENT_OPERATORS,
    CONSUMPTION_AGENT_TYPE_INVESTIGATIONS,
    CONSUMPTION_AGENT_TYPES,
    ConsumptionAgentInvestigationFormGroup,
    ConsumptionAgentSchedule,
    ConsumptionAgentType,
} from '../create-consumption-agent/consumption-agent-investigation/consumption-agent-investigation.model';
import {
    AGENT_CONSUMPTION_TYPES,
    AgentMonitoringFormGroup,
    consumptionToMonitorEntries,
} from '../create-consumption-agent/consumption-agent-monitoring/consumption-agent-monitoring.model';
import {DateTime} from 'luxon';

export const baseConsumptionAgentSchema = z.object({
    reportId: zNumberToString.nullable(),
    agentType: z.enum(CONSUMPTION_AGENT_TYPES),
    consumptionToMonitor: z.enum(AGENT_CONSUMPTION_TYPES),
    operator: z.enum(CONSUMPTION_AGENT_OPERATORS),
    schedule: z.enum(AGENT_SCHEDULES),
    dayCount: z.number(),
    showConsumptionForMeters: z.boolean(),
    meterCriteriaMismatch: z.boolean(),
    sendBlank: z.boolean(),
    createdDate: zDateTime,
    agentName: z
        .string()
        .nullable()
        .transform(val => val ?? ''),
    recipients: z
        .array(z.string())
        .nullable()
        .transform(val => val ?? []),
});

const meterGroupSchema = z.object({
    groupName: z.string().default(''),
    idsInGroup: z.array(z.number()).default([]),
});

const defaultMeterGroup = {
    groupName: '',
    idsInGroup: [],
};

const meterSettingsSchema = z.object({
    outputUnit: zNumberToString,
    meterSuperTypeNo: zNumberToString,
    usageAndUnits: z
        .array(
            z.object({
                alloUnitType: z.string().default(''),
                unit: zNumberToString,
            }),
        )
        .default([]),
    meterGroup: meterGroupSchema.nullish().transform(val => val ?? defaultMeterGroup),
    meterGroup2: meterGroupSchema.nullish().transform(val => val ?? defaultMeterGroup),
});

const fixedValueAgentSchema = z.object({
    fixedValue: z
        .number()
        .nullable()
        .transform(val => (val === null ? undefined : val)),
});

const percentageAgentSchema = z.object({
    percentage: z
        .number()
        .nullable()
        .transform(val => (val === null ? undefined : val)),
});

const increaseDecreaseAgentSchema = z.object({
    ...percentageAgentSchema.shape,
    comparisonPeriod: z
        .enum(CONSUMPTION_AGENT_COMPARISON_PERIODS)
        .nullable()
        .transform(val => (val === null ? undefined : val)),
    minUnitCount: z
        .number()
        .nullable()
        .transform(val => (val === null ? undefined : val)),
});

const groupComparisonAgentSchema = z.object({
    // unfilled should be handled as '0' according to AGI-14954
    percentage: percentageAgentSchema.shape.percentage.transform(val =>
        val === 0 ? undefined : val,
    ),
});

export const consumptionAgentSchema = baseConsumptionAgentSchema
    .extend({
        meterSettings: meterSettingsSchema,
    })
    .extend({
        ...fixedValueAgentSchema.partial().shape,
        ...increaseDecreaseAgentSchema.partial().shape,
        ...groupComparisonAgentSchema.partial().shape,
    });

export type ConsumptionAgent = z.infer<typeof consumptionAgentSchema>;

export type ConsumptionAgentTranslated = ConsumptionAgent & {
    investigation: string;
    consumptionToMonitorTranslated: string;
    usageAndUnitsTranslated: string;
};

export type ConsumptionAgentRequest = {
    reportId?: string;
    agentType: ConsumptionAgentType;
    agentName: string;
    dayCount: number;
    consumptionToMonitor: string;
    operator: string;
    meterSettings: {
        meterSuperTypeNo: number;
        outputUnit: number;
        usageAndUnits: {
            alloUnitType: string;
            unit: number;
        }[];
        meterGroup: {
            groupName: string;
            idsInGroup: number[];
        };
        meterGroup2?: {
            groupName: string;
            idsInGroup: number[];
        };
    };
    schedule: string;
    sendBlank: boolean;
    showConsumptionForMeters: boolean;
    recipients?: string[];
    comparisonPeriod?: string;
    value?: number;
    percentage?: number;
    minUnitCount?: number;
};

export type ConsumptionAgentRequestResultBody = {
    operator: string;
    dayCount: number;
    meterSettings: {
        outputUnit: number;
        meterGroup: {
            groupName: string;
            idsInGroup: number[];
        };
        meterGroup2?: {
            groupName: string;
            idsInGroup: number[];
        };
    };
    comparisonPeriod?: string;
    percentage?: number;
    minUnitCount?: number;
    fixedValue?: number;
    showConsumptionForMeters?: boolean;
};
export const convertableUnit = z.object({
    unitId: zNumberToString,
    listOfConvertableUnits: z.array(zNumberToString),
});

export type ConvertableUnit = z.infer<typeof convertableUnit>;

export type ConsumptionAgentFormGroup = FormGroup<{
    submitted: FormControl<boolean>;
    agentPreviewed: FormControl<boolean>;
    agentName: FormControl<string>;
    dayCount: FormControl<number | null>;
    monitoringFormGroup: AgentMonitoringFormGroup;
    investigationFormGroup: ConsumptionAgentInvestigationFormGroup;
    recipients: FormControl<string[]>;
    schedule: FormControl<ConsumptionAgentSchedule>;
    sendBlank: FormControl<boolean>;
    showConsumptionForMeters: FormControl<boolean>;
}>;

export function createUpdateAgentRequest(
    formGroup: ConsumptionAgentFormGroup,
): ConsumptionAgentRequest {
    const values = formGroup.getRawValue();
    const investigationValues = formGroup.controls.investigationFormGroup.value;
    const {selectMetersForAgentFormGroup} = formGroup.controls.monitoringFormGroup.controls;
    const meterSettings =
        selectMetersForAgentFormGroup.controls.meterSettingsFormGroup.getRawValue();
    const {idsInGroup1, idsInGroup2} =
        selectMetersForAgentFormGroup.getRawValue().meterIdGroupsFormGroup;
    const {consumptionToMonitor} = values.monitoringFormGroup;

    const usageAndUnits = meterSettings.selectedUsageAndUnits.map(({alloUnitType, unit}) => ({
        alloUnitType,
        unit: Number(unit),
    }));

    return {
        agentType: values.investigationFormGroup.agentType,
        agentName: values.agentName,
        dayCount: Number(values.dayCount),
        consumptionToMonitor: consumptionToMonitor,
        meterSettings: {
            outputUnit: Number(meterSettings.outputUnit),
            meterSuperTypeNo: Number(meterSettings.meterSuperTypeNo),
            usageAndUnits: usageAndUnits,
            meterGroup: {
                groupName: meterSettings.groupName,
                idsInGroup: idsInGroup1,
            },
            ...(consumptionToMonitor === 'DIFFERENCECONSUMPTION' && {
                meterGroup2: {
                    groupName: meterSettings.groupName2,
                    idsInGroup: idsInGroup2,
                },
            }),
        },
        schedule: values.schedule,
        sendBlank: values.sendBlank,
        operator: values.investigationFormGroup.operator,
        showConsumptionForMeters:
            consumptionToMonitor !== 'INDIVIDUALCONSUMPTION' && values.showConsumptionForMeters,
        ...(values.recipients.length > 0 && {
            recipients: values.recipients,
        }),
        ...(investigationValues.fixedValue != null && {
            fixedValue: Number(investigationValues.fixedValue),
        }),
        ...(investigationValues.minUnitCount != null && {
            minUnitCount: Number(investigationValues.minUnitCount),
        }),
        ...(investigationValues.percentage != null && {
            percentage: Number(investigationValues.percentage),
        }),
        ...(investigationValues.agentType === 'INCREASING_DECREASING' && {
            comparisonPeriod: investigationValues.comparisonPeriod,
        }),
    };
}

export function setupConsumptionAgentRequestResultBody(
    consumptionAgentRequest: ConsumptionAgentTranslated | null,
): ConsumptionAgentRequestResultBody | null {
    if (consumptionAgentRequest === null) {
        return null;
    }
    const shared = {
        operator: consumptionAgentRequest.operator,
        dayCount: Number(consumptionAgentRequest.dayCount),
        meterSettings: {
            meterSuperTypeNo: Number(consumptionAgentRequest.meterSettings.meterSuperTypeNo),
            outputUnit: Number(consumptionAgentRequest.meterSettings.outputUnit),
            meterGroup: {
                groupName: consumptionAgentRequest.meterSettings.meterGroup.groupName,
                idsInGroup: consumptionAgentRequest.meterSettings.meterGroup.idsInGroup,
            },
            ...(consumptionAgentRequest.consumptionToMonitor === 'DIFFERENCECONSUMPTION' && {
                meterGroup2: {
                    groupName: consumptionAgentRequest.meterSettings.meterGroup2.groupName,
                    idsInGroup: consumptionAgentRequest.meterSettings.meterGroup2.idsInGroup,
                },
            }),
            usageAndUnits: consumptionAgentRequest.meterSettings.usageAndUnits.map(
                ({alloUnitType, unit}) => ({
                    alloUnitType,
                    unit: Number(unit),
                }),
            ),
        },
    };
    const summedConsumptionFixed = {
        fixedValue: consumptionAgentRequest.fixedValue ?? undefined,
        showConsumptionForMeters: consumptionAgentRequest.showConsumptionForMeters,
    };
    const individualFixed = {
        fixedValue: consumptionAgentRequest.fixedValue ?? undefined,
    };
    const percentage = {
        ...(consumptionAgentRequest.percentage != null && {
            percentage: consumptionAgentRequest.percentage,
        }),
        ...(consumptionAgentRequest.minUnitCount != null && {
            minUnitCount: consumptionAgentRequest.minUnitCount,
        }),
    };
    const summedConsumptionPercentage = {
        ...(consumptionAgentRequest.comparisonPeriod != null && {
            comparisonPeriod: consumptionAgentRequest.comparisonPeriod,
        }),
        ...percentage,
        showConsumptionForMeters: consumptionAgentRequest.showConsumptionForMeters,
    };
    const individualPercentage = {
        ...(consumptionAgentRequest.comparisonPeriod != null && {
            comparisonPeriod: consumptionAgentRequest.comparisonPeriod,
        }),
        ...percentage,
    };
    const twoGroupsFixed = {
        fixedValue: consumptionAgentRequest.fixedValue ?? undefined,
        showConsumptionForMeters: consumptionAgentRequest.showConsumptionForMeters,
    };
    const twoGroupPercentage = {
        comparisonPeriod: consumptionAgentRequest.comparisonPeriod ?? undefined,
        ...percentage,
        showConsumptionForMeters: consumptionAgentRequest.showConsumptionForMeters,
    };
    const twoGroupsCompare = {
        percentage: consumptionAgentRequest.percentage ?? 0,
        showConsumptionForMeters: consumptionAgentRequest.showConsumptionForMeters,
    };
    return {
        ...shared,
        // INDIVIDUAL CONSUMPTION REQUESTS
        ...(consumptionAgentRequest.consumptionToMonitor === 'INDIVIDUALCONSUMPTION' &&
            consumptionAgentRequest.agentType === 'FIXED_VALUE' &&
            individualFixed),
        ...(consumptionAgentRequest.consumptionToMonitor === 'INDIVIDUALCONSUMPTION' &&
            consumptionAgentRequest.agentType === 'INCREASING_DECREASING' &&
            individualPercentage),
        // SUMMED CONSUMPTION REQUESTS
        ...(consumptionAgentRequest.consumptionToMonitor === 'SUMMEDCONSUMPTION' &&
            consumptionAgentRequest.agentType === 'FIXED_VALUE' &&
            summedConsumptionFixed),
        ...(consumptionAgentRequest.consumptionToMonitor === 'SUMMEDCONSUMPTION' &&
            consumptionAgentRequest.agentType === 'INCREASING_DECREASING' &&
            summedConsumptionPercentage),
        // COMPARE GROUPS REQUEST
        ...(consumptionAgentRequest.consumptionToMonitor === 'DIFFERENCECONSUMPTION' &&
            consumptionAgentRequest.agentType === 'FIXED_VALUE' &&
            twoGroupsFixed),
        ...(consumptionAgentRequest.consumptionToMonitor === 'DIFFERENCECONSUMPTION' &&
            consumptionAgentRequest.agentType === 'INCREASING_DECREASING' &&
            twoGroupPercentage),
        ...(consumptionAgentRequest.consumptionToMonitor === 'DIFFERENCECONSUMPTION' &&
            consumptionAgentRequest.agentType === 'GROUP_COMPARISON' &&
            twoGroupsCompare),
    };
}

export function mapFormGroupToConsumptionAgentTranslated(
    formGroup: ConsumptionAgentFormGroup,
    translateService: TranslateService,
): ConsumptionAgentTranslated {
    const formValues = formGroup.getRawValue();
    const {selectMetersForAgentFormGroup} = formGroup.controls.monitoringFormGroup.controls;
    const meterSettings =
        selectMetersForAgentFormGroup.controls.meterSettingsFormGroup.getRawValue();
    const {idsInGroup1, idsInGroup2} =
        selectMetersForAgentFormGroup.getRawValue().meterIdGroupsFormGroup;

    const usageAndUnits = meterSettings.selectedUsageAndUnits.map(({alloUnitType, unit}) => ({
        alloUnitType,
        unit: Number(unit),
    }));
    return {
        meterSettings: {
            meterGroup: {
                groupName:
                    selectMetersForAgentFormGroup.controls.meterSettingsFormGroup.getRawValue()
                        .groupName,
                idsInGroup: idsInGroup1,
            },
            meterGroup2: {
                groupName:
                    selectMetersForAgentFormGroup.controls.meterSettingsFormGroup.getRawValue()
                        .groupName2,
                idsInGroup: idsInGroup2,
            },
            outputUnit:
                formValues.monitoringFormGroup.selectMetersForAgentFormGroup.meterSettingsFormGroup
                    .outputUnit,
            meterSuperTypeNo:
                formValues.monitoringFormGroup.selectMetersForAgentFormGroup.meterSettingsFormGroup
                    .meterSuperTypeNo,
            usageAndUnits: usageAndUnits.map(usageAndUnit => ({
                alloUnitType: usageAndUnit.alloUnitType,
                unit: String(usageAndUnit.unit),
            })),
        },
        consumptionToMonitor: formValues.monitoringFormGroup.consumptionToMonitor,
        operator: formValues.investigationFormGroup.operator,
        consumptionToMonitorTranslated: translateService.translate(
            consumptionToMonitorEntries.find(
                it => it.type === formValues.monitoringFormGroup.consumptionToMonitor,
            )?.translationKey ?? '',
        ),
        fixedValue: formValues.investigationFormGroup.fixedValue ?? undefined,
        reportId: null,
        agentType: formValues.investigationFormGroup.agentType,
        meterCriteriaMismatch: false,
        createdDate: DateTime.now().toUTC(), // Ensure this is a valid DateTime<true> instance
        usageAndUnitsTranslated: usageAndUnits.map(
            usageAndUnit =>
                `${translateService.translate(`mappers_allocationUnitMap.${usageAndUnit.alloUnitType}`, 'online', 'common')} - ${translateService.translate(`mappers_measurementUnit.${usageAndUnit.unit}`, 'online', 'common')}`,
        )[0],
        schedule: formValues.schedule,
        dayCount: formValues.dayCount ?? 0,
        showConsumptionForMeters: formValues.showConsumptionForMeters,
        sendBlank: formValues.sendBlank,
        minUnitCount: formValues.investigationFormGroup.minUnitCount ?? undefined,
        agentName: formValues.agentName,
        recipients: formValues.recipients,
        comparisonPeriod: formValues.investigationFormGroup.comparisonPeriod,
        percentage: formValues.investigationFormGroup.percentage ?? undefined,
        investigation: translateService.translate(
            CONSUMPTION_AGENT_TYPE_INVESTIGATIONS.find(
                invest => invest.key === formValues.investigationFormGroup.agentType,
            )?.labelKey ?? '',
        ),
    };
}

export type ConsumptionAgentExport = {
    blob: Blob;
    fileName: string;
};
